import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '@/features/game/store/gameStore';
import { playerQuestionService, GameQuestionDto } from '@/features/game/services/questionService';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Logo } from '@/components/shared/Logo';
import { 
  Eye, ArrowRight, 
  CheckCircle, MinusCircle, AlertCircle, Timer
} from 'lucide-react';

import { API_BASE_URL } from '@/lib/api/apiClient';
const API_URL = API_BASE_URL || '';

// Helper to get full image URL (returns empty string for missing path so img src is always string)
const getImageUrl = (path?: string | null): string => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const base = API_URL || '';
  const p = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${p}` : p;
};

export const QuestionPage = () => {
  const navigate = useNavigate();
  const {
    gameSettings,
    team1,
    team2,
    currentTurn,
    currentCell,
    nextQuestionIsPit,
    setNextQuestionIsPit,
    switchTurn,
    addScore,
    subtractScore,
    markCellAnswered,
    useTeamLifeline,
    shownQuestionIds,
    addShownQuestionId,
  } = useGameStore();

  const [question, setQuestion] = useState<GameQuestionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showWhoAnswered, setShowWhoAnswered] = useState(false);
  const [lifelineUsedThisQuestion, setLifelineUsedThisQuestion] = useState(false);

  // Fetch question when component mounts
  useEffect(() => {
    if (currentCell) {
      fetchQuestion();
    }
  }, [currentCell]);

  // Redirect if no current cell
  useEffect(() => {
    if (!currentCell || !gameSettings || !team1 || !team2) {
      navigate('/player/game');
    }
  }, [currentCell, gameSettings, team1, team2, navigate]);

  // Timer effect
  useEffect(() => {
    if (!isTimerRunning || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setIsTimerRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isTimerRunning, timeRemaining]);

  const fetchQuestion = async () => {
    if (!currentCell) return;

    try {
      setLoading(true);
      setError(null);

      // Map points to difficulty
      const difficultyMap: Record<number, 'Easy' | 'Medium' | 'Hard'> = {
        200: 'Easy',
        400: 'Medium',
        600: 'Hard',
      };
      const difficulty = difficultyMap[currentCell.points] || 'Easy';

      // جلب كل أسئلة الفئة (بدون فلتر التجربة المجانية) حتى تظهر الأسئلة المضافة
      const fetchedQuestion = await playerQuestionService.getRandomQuestion(
        currentCell.categoryId,
        difficulty,
        false, // isFreeTrial = false ليعرض كل الأسئلة المفعّلة للفئة
        false, // isPromotion
        shownQuestionIds
      );

      if (fetchedQuestion) {
        setQuestion(fetchedQuestion);
        addShownQuestionId(fetchedQuestion.id);
        setTimeRemaining(fetchedQuestion.timeLimit || 30);
        setIsTimerRunning(true);
      } else {
        // Use fallback question if no questions available (e.g. no EnableForNewUsers in free trial)
        setQuestion({
          id: `fallback-${Date.now()}`,
          questionText: `سؤال من فئة ${currentCell.categoryName}`,
          questionTextAr: `ما هي عاصمة المملكة العربية السعودية؟`,
          answer: 'Riyadh',
          answerAr: 'الرياض',
          explanation: 'الرياض هي عاصمة المملكة العربية السعودية',
          explanationAr: 'الرياض هي العاصمة وأكبر مدينة في المملكة العربية السعودية',
          difficulty: difficulty,
          timeLimit: 30,
          points: currentCell.points,
          categoryName: currentCell.categoryName,
        });
        setTimeRemaining(30);
        setIsTimerRunning(true);
        setError('لا توجد أسئلة متاحة في هذه الفئة، تم استخدام سؤال بديل');
      }
    } catch (err) {
      console.error('Failed to fetch question:', err);
      setError('فشل في جلب السؤال');
      // Use fallback
      setQuestion({
        id: `fallback-${Date.now()}`,
        questionText: `Question from ${currentCell.categoryName}`,
        questionTextAr: `ما هي عاصمة المملكة العربية السعودية؟`,
        answer: 'Riyadh',
        answerAr: 'الرياض',
        explanationAr: 'الرياض هي العاصمة وأكبر مدينة في المملكة',
        difficulty: 'Easy',
        timeLimit: 30,
        points: currentCell.points,
        categoryName: currentCell.categoryName,
      });
      setTimeRemaining(30);
      setIsTimerRunning(true);
    } finally {
      setLoading(false);
    }
  };

  if (!currentCell || !gameSettings || !team1 || !team2) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600 mt-4">جاري تحميل السؤال...</p>
        </div>
      </div>
    );
  }

  // Handle showing the answer
  const handleShowAnswer = () => {
    setIsTimerRunning(false);
    setShowAnswer(true);
    setShowWhoAnswered(true);
  };

  // Normal answer: add points only (no deduction)
  const handleWhoAnswered = (answeredBy: 'team1' | 'team2' | 'none') => {
    const points = currentCell.points;

    if (answeredBy === 'team1') {
      addScore('team1', points);
      markCellAnswered(currentCell.categoryId, points, currentCell.side, 'team1');
    } else if (answeredBy === 'team2') {
      addScore('team2', points);
      markCellAnswered(currentCell.categoryId, points, currentCell.side, 'team2');
    } else {
      markCellAnswered(currentCell.categoryId, points, currentCell.side);
    }

    setNextQuestionIsPit(false);
    switchTurn();
    navigate('/player/game');
  };

  // الحفرة: الفائز +نقاط، الخاسر -نقاط (تُفعّل قبل الدخول للسؤال)
  const handlePit = (winner: 'team1' | 'team2') => {
    const points = currentCell.points;
    const loser = winner === 'team1' ? 'team2' : 'team1';

    addScore(winner, points);
    subtractScore(loser, points); // خصم من الخاسر (يسمح بالسالب)
    markCellAnswered(currentCell.categoryId, points, currentCell.side, winner);

    setNextQuestionIsPit(false);
    switchTurn();
    navigate('/player/game');
  };

  // وسيلة مساعدة واحدة فقط لكل سؤال
  const handleUseLifeline = (lifelineId: string) => {
    if (lifelineUsedThisQuestion) return;
    const currentTeamId = currentTurn;
    useTeamLifeline(currentTeamId, lifelineId);
    setLifelineUsedThisQuestion(true);
  };

  // Check if lifeline is available for current team
  const isLifelineAvailable = (lifelineId: string) => {
    if (currentTurn === 'team1') {
      return !team1.usedLifelines.includes(lifelineId);
    }
    return !team2.usedLifelines.includes(lifelineId);
  };

  // Format timer
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get current category info
  const currentCategory = gameSettings.categories.find(c => c.id === currentCell.categoryId);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col" dir="rtl">
      {/* Top Header Bar - Brand Identity */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-4 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/player/game')}
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
            >
              <ArrowRight className="w-5 h-5" />
            </button>
            <Logo size="sm" showName={true} />
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-yellow-400 text-green-900 px-3 py-1 rounded-full font-bold text-sm shadow">
              {currentCell.points} نقطة
            </span>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-yellow-100 border-b border-yellow-300 text-yellow-800 px-4 py-2 flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Teams Header with Timer */}
      <div className="flex items-stretch">
        {/* Team 1 */}
        <div className={`flex-1 p-3 flex items-center justify-between transition-all ${
          currentTurn === 'team1' 
            ? 'bg-red-50 border-b-4 border-red-500' 
            : 'bg-white border-b border-gray-200'
        }`}>
          <div>
            <p className={`font-bold text-sm ${currentTurn === 'team1' ? 'text-red-600' : 'text-gray-500'}`}>
              {team1.name}
            </p>
            <p className="text-2xl font-bold text-red-600">{team1.score}</p>
          </div>
          {currentTurn === 'team1' && (
            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">دورك</span>
          )}
        </div>

        {/* Timer Center - Green Theme */}
        <div className={`flex items-center justify-center px-6 py-2 ${
          timeRemaining > 10 
            ? 'bg-gradient-to-b from-green-500 to-green-600' 
            : timeRemaining > 5 
              ? 'bg-gradient-to-b from-yellow-500 to-yellow-600'
              : 'bg-gradient-to-b from-red-500 to-red-600 animate-pulse'
        }`}>
          <div className="flex flex-col items-center">
            <Timer className="w-5 h-5 text-white/80 mb-1" />
            <span className="text-white font-mono font-bold text-2xl drop-shadow">
              {formatTime(timeRemaining)}
            </span>
          </div>
        </div>

        {/* Team 2 */}
        <div className={`flex-1 p-3 flex items-center justify-between transition-all ${
          currentTurn === 'team2' 
            ? 'bg-green-50 border-b-4 border-green-500' 
            : 'bg-white border-b border-gray-200'
        }`}>
          {currentTurn === 'team2' && (
            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">دورك</span>
          )}
          <div className="text-left">
            <p className={`font-bold text-sm ${currentTurn === 'team2' ? 'text-green-600' : 'text-gray-500'}`}>
              {team2.name}
            </p>
            <p className="text-2xl font-bold text-green-600">{team2.score}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col p-4 md:p-6">
        {/* Category Info with Image */}
        <div className="flex items-center gap-4 mb-4">
          {currentCategory && (
            <div className="w-16 h-16 rounded-xl overflow-hidden shadow-md flex-shrink-0 border-2 border-green-200">
              <img
                src={getImageUrl(currentCategory.imageUrl) || (API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg')}
                alt={currentCategory.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg';
                }}
              />
            </div>
          )}
          <div>
            <p className="text-green-600 text-xs font-medium">الفئة</p>
            <h2 className="text-lg font-bold text-gray-800">{currentCell.categoryName}</h2>
          </div>
        </div>

        {/* Question Card */}
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-white rounded-3xl p-5 md:p-8 max-w-3xl w-full shadow-lg border border-green-100">
            {/* Question Image */}
            {question?.imageUrl && (
              <div className="mb-6 flex justify-center">
                <div className="rounded-2xl overflow-hidden shadow-md border border-gray-200">
                  <img
                    src={getImageUrl(question.imageUrl ?? undefined)}
                    alt="صورة السؤال"
                    className={`object-contain ${
                      question.imageSize === 'small' ? 'max-w-[200px] max-h-[150px]' :
                      question.imageSize === 'large' ? 'max-w-[500px] max-h-[400px]' : 'max-w-[350px] max-h-[280px]'
                    }`}
                  />
                </div>
              </div>
            )}

            {/* Question Text */}
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 text-center leading-relaxed">
              {question?.questionTextAr || question?.questionText}
            </h2>

            {/* Answer (when shown) */}
            {showAnswer && question && (
              <div className="mt-6 p-5 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-400 shadow-inner">
                <p className="text-sm text-green-600 font-bold mb-3 text-center">الإجابة الصحيحة</p>
                
                {/* Answer Image (if exists) */}
                {question.answerImageUrl && (
                  <div className="mb-4 flex justify-center">
                    <div className="rounded-xl overflow-hidden shadow-md border border-green-200">
                      <img
                        src={getImageUrl(question.answerImageUrl ?? undefined)}
                        alt="صورة الإجابة"
                        className="max-w-[300px] max-h-[200px] object-contain"
                      />
                    </div>
                  </div>
                )}
                
                <p className="text-2xl md:text-3xl font-bold text-green-700 text-center">
                  {question.answerAr || question.answer}
                </p>
                {(question.explanationAr || question.explanation) && (
                  <p className="text-gray-600 text-center mt-4 text-sm bg-white/50 rounded-lg p-3">
                    {question.explanationAr || question.explanation}
                  </p>
                )}
              </div>
            )}

            {/* Who Answered Buttons - Inside the card */}
            {showWhoAnswered && (
              <div className="mt-6 pt-5 border-t-2 border-green-200">
                <p className="text-center text-gray-700 text-lg font-bold mb-4">من أجاب صحيحاً؟</p>
                <div className="flex flex-col gap-3">
                  {nextQuestionIsPit ? (
                    /* الحفرة: تُفعّل قبل السؤال - خصم من الخاسر وإضافة للفائز */
                    <>
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handlePit('team1')}
                          className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-3 py-4 text-base font-bold rounded-xl flex-1 justify-center shadow-md transition-all hover:shadow-lg"
                        >
                          <CheckCircle className="w-5 h-5 ml-2" />
                          {team1.name}
                          <span className="mr-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                            +{currentCell.points} / -{currentCell.points}
                          </span>
                        </Button>
                        <Button
                          onClick={() => handlePit('team2')}
                          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-3 py-4 text-base font-bold rounded-xl flex-1 justify-center shadow-md transition-all hover:shadow-lg"
                        >
                          <CheckCircle className="w-5 h-5 ml-2" />
                          {team2.name}
                          <span className="mr-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                            +{currentCell.points} / -{currentCell.points}
                          </span>
                        </Button>
                      </div>
                      <Button
                        onClick={() => handleWhoAnswered('none')}
                        variant="outline"
                        className="border-2 border-gray-300 text-gray-500 hover:bg-gray-50 px-6 py-3 text-base font-bold rounded-xl w-full justify-center"
                      >
                        <MinusCircle className="w-5 h-5 ml-2" />
                        لا أحد
                      </Button>
                    </>
                  ) : (
                    /* إجابة عادية: إضافة النقاط للفريق الفائز فقط */
                    <>
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleWhoAnswered('team1')}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-5 text-lg font-bold rounded-xl flex-1 justify-center shadow-md"
                        >
                          <CheckCircle className="w-5 h-5 ml-2" />
                          {team1.name}
                          <span className="mr-2 text-sm bg-white/20 px-2 py-1 rounded">+{currentCell.points}</span>
                        </Button>
                        <Button
                          onClick={() => handleWhoAnswered('team2')}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-5 text-lg font-bold rounded-xl flex-1 justify-center shadow-md"
                        >
                          <CheckCircle className="w-5 h-5 ml-2" />
                          {team2.name}
                          <span className="mr-2 text-sm bg-white/20 px-2 py-1 rounded">+{currentCell.points}</span>
                        </Button>
                      </div>
                      <Button
                        onClick={() => handleWhoAnswered('none')}
                        variant="outline"
                        className="border-2 border-gray-300 text-gray-500 hover:bg-gray-50 px-6 py-3 text-base font-bold rounded-xl w-full justify-center"
                      >
                        <MinusCircle className="w-5 h-5 ml-2" />
                        لا أحد
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 space-y-3">
          {/* Show Answer Button */}
          {!showAnswer && (
            <div className="flex justify-center">
              <Button
                onClick={handleShowAnswer}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-10 py-5 text-lg font-bold rounded-2xl shadow-lg transition-all hover:shadow-xl hover:scale-105"
              >
                <Eye className="w-6 h-6 ml-2" />
                الإجابة
              </Button>
            </div>
          )}

          {/* عند الدخول للسؤال: كل الوسائل من الداخل ما عدا الحفرة (3 وسائل). أقصى 3/4: إن اختار الحفرة = 2 من داخل، وإلا 3 من داخل */}
          {!showAnswer && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-100">
              <p className="text-center text-green-700 text-sm font-medium mb-3">
                وسائل المساعدة - {currentTurn === 'team1' ? team1.name : team2.name} (وسيلة واحدة لهذا السؤال، المجموع 3/4)
              </p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                {(() => {
                  const currentTeam = currentTurn === 'team1' ? team1 : team2;
                  const allLifelines = gameSettings?.lifelines || currentTeam.lifelines || [];
                  // كل الوسائل من الداخل ما عدا الحفرة (3 وسائل ظاهرة دائماً)
                  const lifelinesInsideOnly = allLifelines.filter(
                    (l) => (l as { code?: string }).code !== 'THE_PIT'
                  );
                  const teamChosenIds = (currentTeam.lifelines || []).map((l) => l.id);
                  return lifelinesInsideOnly.map((lifeline) => {
                    const isChosen = teamChosenIds.includes(lifeline.id);
                    const canUse = isChosen && isLifelineAvailable(lifeline.id) && !lifelineUsedThisQuestion;
                    const teamColor = currentTurn === 'team1' ? 'red' : 'green';
                    return (
                      <Button
                        key={lifeline.id}
                        onClick={() => canUse && handleUseLifeline(lifeline.id)}
                        disabled={!canUse}
                        variant="outline"
                        title={!isChosen ? `${lifeline.nameAr} (غير مختار - 3/4 فقط)` : undefined}
                        className={`rounded-xl transition-all text-sm px-3 py-2 ${
                          !canUse
                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                            : teamColor === 'red'
                              ? 'border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 bg-white'
                              : 'border-green-300 text-green-600 hover:bg-green-50 hover:border-green-400 bg-white'
                        }`}
                      >
                        {lifeline.iconUrl && (
                          <img 
                            src={getImageUrl(lifeline.iconUrl ?? undefined)} 
                            alt="" 
                            className={`w-5 h-5 rounded ml-2 ${!canUse ? 'grayscale' : ''}`}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                        {lifeline.nameAr}
                        {!isChosen && <span className="text-[10px] mr-1">(غير مختار)</span>}
                      </Button>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
